import { Box, Typography } from "@mui/material";
import React, { useContext, useMemo } from "react";
import { Helmet } from "react-helmet";
import { useRouteLoaderData } from "react-router-dom";
import ProductsContainer from "../../../components/app/Products/ProductsContainer";
import AllCategoriesContext from "../../../context/allCategories/AllCategoriesContext";
import MoreProducts from "../../../components/app/Others/MoreProducts";
import CategoryExploreGrid from "../../../components/app/Home/CategoryExploreGrid";
import TrustBar from "../../../components/app/Home/TrustBar";
import CtaBanner from "../../../components/app/Home/CtaBanner";
import ShopByProperties, {
  defaultProperties,
} from "../../../components/app/Home/ShopByProperties";
import ImageCarousel from "../../../components/common/ImageCarousel";
import slide1 from "../../../assets/images/slider/slide-1.webp";
import slide2 from "../../../assets/images/slider/slide-2.webp";
import slide3 from "../../../assets/images/slider/slide-3.webp";
import { categoryHasDirectProducts } from "../../../utils/categoryProducts";
import { loadStorefrontData } from "../../../utils/storefrontBootstrap";
import "./styles.css";

const defaultHeroSlides = [slide1, slide2, slide3];

const Homepage = () => {
  const loaderData = useRouteLoaderData("storefront");
  const productsArray = useMemo(
    () => loaderData.data || [],
    [loaderData.data]
  );
  const storefrontData = loaderData.storefront || {};

  const allCategoriesContext = useContext(AllCategoriesContext);
  const allCategoriesArr = storefrontData.categories || allCategoriesContext.state;
  const parentCategories = useMemo(
    () =>
      allCategoriesArr.filter(
        (item) => item?.parentCategory?.id === "None"
      ),
    [allCategoriesArr]
  );
  const heroSlides = useMemo(() => {
    const activeSliders = (storefrontData.sliders || [])
      .filter((item) => item.isActive !== false && item.imageUrl)
      .sort((a, b) => (a.order || 0) - (b.order || 0))
      .map((item) => ({
        type: "image",
        url: item.imageUrl,
        link: item.link || "",
        title: item.title || "",
      }));

    return activeSliders.length > 0 ? activeSliders : defaultHeroSlides;
  }, [storefrontData.sliders]);

  const visibleCategories = useMemo(() => {
    return parentCategories.filter((category) =>
      categoryHasDirectProducts(category.id, productsArray),
    );
  }, [parentCategories, productsArray]);

  const categoriesLoading = allCategoriesArr.length === 0;
  const brandName = process.env.REACT_APP_NAME || "Inproveda";

  return (
    <>
      <Helmet>
        <title>{brandName} | 100% Herbal & Ayurvedic Products</title>
      </Helmet>
      <Box component="main" className="home-page" sx={{ width: "100%" }}>
        <Box className="hero-carousel home-banner">
          <ImageCarousel
            key={heroSlides.map((s) => (typeof s === "string" ? s : s.url)).join(";")}
            ImagesArr={heroSlides}
            autoPlay={true}
            hero={true}
          />
        </Box>
        <TrustBar />
        <ShopByProperties
          properties={storefrontData.properties}
          enabled={
            storefrontData.propertiesSettings?.isEnabled ??
            storefrontData.propertiesSettings?.enabled
          }
        />
        {(categoriesLoading || visibleCategories.length > 0) && (
          <Box className="home-band home-band-muted">
            <Box className="store-shell">
              <Box className="store-row store-row-first">
                <Box sx={{ mb: { xs: 4, md: 5.5 }, textAlign: "center" }}>
                  <Typography
                    variant="overline"
                    sx={{
                      color: "#10b981",
                      fontWeight: 700,
                      letterSpacing: "0.12em",
                      fontSize: "0.75rem",
                    }}
                  >
                    Curated Collections
                  </Typography>
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 800,
                      color: "#0f2820",
                      mt: 0.5,
                      fontSize: { xs: "1.6rem", md: "2rem" },
                      letterSpacing: "-0.02em",
                    }}
                  >
                    Shop By Category
                  </Typography>
                  <Box
                    sx={{
                      width: 48,
                      height: 3,
                      bgcolor: "#10b981",
                      borderRadius: 2,
                      mx: "auto",
                      mt: 1.5,
                    }}
                  />
                </Box>
                <CategoryExploreGrid
                  categories={visibleCategories}
                  loading={categoriesLoading}
                />
              </Box>
            </Box>
          </Box>
        )}

        {productsArray.length > 0 && (
          <Box className="home-band home-band-latest-products">
            <Box className="store-shell store-shell-latest-products">
              <Box className="store-row">
                <Box sx={{ mb: { xs: 3.5, md: 5.5 }, textAlign: "center", px: { xs: 2, sm: 0 } }}>
                  <Typography
                    variant="overline"
                    sx={{
                      color: "#10b981",
                      fontWeight: 700,
                      letterSpacing: "0.12em",
                      fontSize: "0.75rem",
                    }}
                  >
                    New Arrivals
                  </Typography>
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 800,
                      color: "#0f2820",
                      mt: 0.5,
                      fontSize: { xs: "1.6rem", md: "2rem" },
                      letterSpacing: "-0.02em",
                    }}
                  >
                    Latest Products
                  </Typography>
                  <Box
                    sx={{
                      width: 48,
                      height: 3,
                      bgcolor: "#10b981",
                      borderRadius: 2,
                      mx: "auto",
                      mt: 1.5,
                    }}
                  />
                </Box>
                <ProductsContainer
                  heading=""
                  headingAlign="left"
                  productsArray={productsArray}
                  showCarousel={false}
                  addToCartEnable={true}
                  showInRow={false}
                  showScrollButtons={false}
                />
              </Box>
            </Box>
          </Box>
        )}

        <Box className="home-band home-band-muted">
          <Box className="store-shell">
            <MoreProducts showRecentlyViewed={true} />
            <CtaBanner />
          </Box>
        </Box>
      </Box>
    </>
  );
};
export const DataLoader = async () => {
  const fallbackImages = [
    ...defaultHeroSlides,
    ...defaultProperties.map((property) => property.image),
  ];
  const storefront = await loadStorefrontData(fallbackImages);
  return { success: true, data: storefront.products, storefront };
};
export default Homepage;
